import { useCallback, useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import Header from './Header';
import Sidebar from './Sidebar';
import { TrialBanner, TrialExpiredBanner } from '@/components/billing';
import { SessionTimeoutWarning } from '@/components/settings';
import { useTrialStatus, useSubscription } from '@/hooks/useBilling';
import { useSessionTimeout } from '@/hooks/useSessionTimeout';
import { useAuthStore } from '@/store/authStore';
import { settingsApi, type SessionPreferences } from '@/services/settings';
import { sessionTimeoutToMs, calculateWarningThreshold } from '@/utils/format';

interface MainLayoutProps {
  showSidebar?: boolean;
}

/**
 * Default session timeout if preferences haven't loaded yet (4 hours).
 */
const DEFAULT_TIMEOUT_OPTION = '4hr' as const;

/**
 * Main layout wrapper for authenticated pages.
 * Includes header, sidebar, trial banner, and session timeout warning.
 *
 * WP-SESSION-3: Activity Tracking & Auto-Logout Warning
 * - Mounts session timeout tracking when user is authenticated
 * - Fetches user's session timeout preference from API
 * - Shows warning modal before session expires
 * - Handles auto-logout on timeout
 */
export default function MainLayout({ showSidebar = true }: MainLayoutProps) {
  const navigate = useNavigate();
  const { logout, isAuthenticated } = useAuthStore();

  // WP-WEBMOB-1: Mobile sidebar drawer state
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  // Fetch trial status and subscription
  const { data: trialStatus } = useTrialStatus();
  const { data: subscription } = useSubscription();

  // Session preferences state
  const [sessionPrefs, setSessionPrefs] = useState<SessionPreferences | null>(null);
  const [prefsLoaded, setPrefsLoaded] = useState(false);

  // Fetch session preferences on mount
  useEffect(() => {
    if (!isAuthenticated) return;

    const fetchSessionPrefs = async () => {
      try {
        const prefs = await settingsApi.getSessionPreferences();
        setSessionPrefs(prefs);
      } catch {
        // Use default timeout if fetch fails
        setSessionPrefs(null);
      } finally {
        setPrefsLoaded(true);
      }
    };

    fetchSessionPrefs();
  }, [isAuthenticated]);

  // Calculate timeout values
  const timeoutOption = sessionPrefs?.sessionTimeout ?? DEFAULT_TIMEOUT_OPTION;
  const timeoutMs = sessionTimeoutToMs(timeoutOption);
  const warningMs = calculateWarningThreshold(timeoutMs);

  // Handle session timeout
  const handleTimeout = useCallback(async () => {
    await logout();
    navigate('/login', {
      state: { sessionExpired: true },
      replace: true,
    });
  }, [logout, navigate]);

  // Session timeout hook
  const { showWarning, remainingMs, extendSession } = useSessionTimeout({
    timeoutMs,
    warningMs,
    onTimeout: handleTimeout,
    // Only enable after preferences are loaded (or attempted) and user is authenticated
    enabled: prefsLoaded && isAuthenticated,
  });

  // Handle manual logout from warning modal
  const handleLogoutNow = useCallback(async () => {
    await logout();
    navigate('/login', { replace: true });
  }, [logout, navigate]);

  // WP-BILLING-20: a tenant holding a subscription (past_due and unpaid included)
  // is a customer, not a lapsed trial; the billing page shows them how to fix
  // a failed payment. Keyed on the subscription, not hasActiveSubscription.
  const hasCurrentSubscription = !!subscription?.subscription;

  // Show trial expired banner if:
  // 1. Trial has expired
  // 2. User does NOT hold a subscription
  const showTrialExpiredBanner =
    trialStatus?.isExpired && !hasCurrentSubscription;

  // Show trial banner only if:
  // 1. User is trialing (isTrialing is true)
  // 2. Trial has NOT expired
  // 3. User does NOT hold a subscription
  // 4. Days remaining is 7 or less
  const showTrialBanner =
    trialStatus?.isTrialing &&
    !trialStatus?.isExpired &&
    !hasCurrentSubscription &&
    trialStatus.daysRemaining <= 7;

  return (
    <div className="min-h-screen bg-gray-50">
      {/*
        WCAG 2.4.1 Bypass Blocks (Level A). Without this, a keyboard or screen
        reader user tabs through the header and every sidebar link before
        reaching the page content, on every single page.

        Visually hidden until focused: sr-only removes it from the layout,
        focus:not-sr-only brings it back for the one moment it is needed.
      */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-white focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-primary-700 focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
      >
        Skip to main content
      </a>
      <Header onMenuToggle={() => setIsMobileSidebarOpen(true)} />
      {/* Trial expired banner - highest priority (WP-BILLING-2) */}
      {showTrialExpiredBanner && <TrialExpiredBanner />}
      {/* Trial warning banner - shown when trial is active but ending soon */}
      {showTrialBanner && trialStatus && (
        <TrialBanner daysRemaining={trialStatus.daysRemaining} />
      )}
      <div className="flex">
        {showSidebar && (
          <Sidebar
            isOpen={isMobileSidebarOpen}
            onClose={() => setIsMobileSidebarOpen(false)}
          />
        )}
        {/* tabIndex={-1} so the skip link can actually move focus here; without
            it the browser scrolls but focus stays on the link. */}
        <main id="main-content" tabIndex={-1} className="flex-1 p-4 md:p-6 focus:outline-none">
          <Outlet />
        </main>
      </div>

      {/* WP-SESSION-3: Session timeout warning modal */}
      <SessionTimeoutWarning
        isOpen={showWarning}
        remainingMs={remainingMs}
        onExtend={extendSession}
        onLogout={handleLogoutNow}
      />
    </div>
  );
}
